///
/// EarthBrowser
///
/// Created by Matt Giger on 1/7/2026
/// Copyright (c) 2026 Geodata Labs
///

import { precipAltitudeScale } from "@/earthbrowser/datasets/precipAltitude"
import { wgs } from "@/earthbrowser/utils/geographic"
import type { matrix } from "@stovokor/math/matrix"
import { clamp } from "@stovokor/math/math"
import type { vec3 } from "@stovokor/math/vector"
import { Renderer } from "@stovokor/engine/Renderer"
import { RenderState } from "@stovokor/engine/RenderState"
import { BlendFactor, DrawMode } from "@stovokor/shader/GLConstants"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type Material } from "@stovokor/shader/Material"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type WebMercatorOverlayTile } from "./WebMercatorOverlayTile"

// Same day/night lighting window as EarthTileRenderer, so the tiles match
// the equirect radar overlay composited into the earth tile shader
const c_fade_range = 3000.0
const c_min_vis_elevation = 1500.0 + wgs.a

export class WebMercatorOverlayTileRenderer extends Renderer {
	private tile: WebMercatorOverlayTile
	private modelViewProjection = new UniformDynamic<matrix>()
	private altitudeScale = new UniformDynamic<number>()
	// Echo opacity ramp shared with overlay_radar / overlay_imerg
	private precipAltitudeScale = new UniformDynamic<number>()
	private lightObjectNormalized = new UniformDynamic<vec3>()

	constructor(context: StovokorContext, tile: WebMercatorOverlayTile, extraMaterials: Material[] = []) {
		const source = new ShaderSourceDefinition("webmercator_tile")
		super(context, source, tile.geometry, [tile.material, ...extraMaterials])
		this.tile = tile

		// The fragment shader outputs premultiplied alpha (required for
		// correct time/LOD blending of transparent echoes), so draw with
		// (ONE, ONE_MINUS_SRC_ALPHA) and restore the engine default after
		this.drawState.blend = [BlendFactor.One, BlendFactor.OneMinusSrcAlpha]
	}

	protected stateUpdate(state: RenderState): void {
		this.materials.forEach(o => o.renderUpdate?.(state))
		this.modelViewProjection.value = state.modelViewProjection
		this.lightObjectNormalized.value = RenderState.lightDirection

		const elevation = state.camera.position.length
		this.altitudeScale.value = clamp((elevation - c_min_vis_elevation) / c_fade_range, 0.0, 1.0)
		this.precipAltitudeScale.value = precipAltitudeScale(elevation - wgs.a)

		this.renderAction = () => {
			this.tile.geometry.indexBuffer.draw(DrawMode.Triangles)
		}
	}
}
